// src/pages/courier/OrderAssignmentSimulator.jsx
import React, { useState, useMemo } from 'react';
import {
  PackageCheck,
  Search,
  Phone,
  User,
  Copy,
  Check,
  AlertTriangle,
  Sparkles
} from 'lucide-react';
import stateDistrictData from '../StatenDistrict.json';
import {
  resolveEligibleCourierBranchesForDistrict,
  createOrderCourierAssignmentSnapshot
} from '../../services/courierService';

const OrderAssignmentSimulator = ({
  couriers = [],
  branches = [],
  serviceAreas = []
}) => {
  const statesList = useMemo(() => stateDistrictData?.states || [], []);

  const [selectedState, setSelectedState] = useState('Odisha');
  const [selectedDistrict, setSelectedDistrict] = useState('Cuttack');
  const [eligibleOptions, setEligibleOptions] = useState([]);
  const [hasResolved, setHasResolved] = useState(false);
  const [selectedBranchForAssignment, setSelectedBranchForAssignment] = useState(null);
  const [snapshotJson, setSnapshotJson] = useState(null);
  const [copied, setCopied] = useState(false);

  // Available districts for the selected state
  const availableDistricts = useMemo(() => {
    const found = statesList.find((s) => s.state.toLowerCase() === selectedState.toLowerCase());
    return found ? found.districts : [];
  }, [statesList, selectedState]);

  const handleStateChange = (e) => {
    const nextState = e.target.value;
    setSelectedState(nextState);
    const found = statesList.find((s) => s.state.toLowerCase() === nextState.toLowerCase());
    if (found && found.districts.length > 0) {
      setSelectedDistrict(found.districts[0]);
    } else {
      setSelectedDistrict('');
    }
    setHasResolved(false);
    setSelectedBranchForAssignment(null);
    setSnapshotJson(null);
  };

  const handleRunResolution = async () => {
    if (!selectedDistrict) return;

    // Run resolver against active service areas, branches, and couriers
    const results = await resolveEligibleCourierBranchesForDistrict({
      districtName: selectedDistrict,
      stateName: selectedState,
      couriersCache: couriers,
      branchesCache: branches,
      serviceAreasCache: serviceAreas
    });

    setEligibleOptions(results);
    setHasResolved(true);
    setSelectedBranchForAssignment(results.length > 0 ? results[0] : null);

    if (results.length > 0) {
      const snap = createOrderCourierAssignmentSnapshot(results[0], 'SUPERADMIN');
      setSnapshotJson(snap);
    } else {
      setSnapshotJson(null);
    }
  };

  const handleSelectOption = (option) => {
    setSelectedBranchForAssignment(option);
    const snap = createOrderCourierAssignmentSnapshot(option, 'SUPERADMIN');
    setSnapshotJson(snap);
  };

  const handleCopySnapshot = () => {
    if (!snapshotJson) return;
    navigator.clipboard.writeText(JSON.stringify(snapshotJson, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="cm-simulator-container">
      {/* Title & info banner */}
      <div className="cm-simulator-header">
        <div className="cm-simulator-title-group">
          <div className="cm-modal-icon-badge">
            <PackageCheck size={24} color="#8a6b32" />
          </div>
          <div>
            <h3 className="cm-simulator-title">Order Assignment Resolution Simulator</h3>
            <p className="cm-simulator-subtitle">
              Test how JMI's checkout/order system resolves eligible courier branches for any delivery
              district and generates immutable assignment snapshots.
            </p>
          </div>
        </div>
      </div>

      {/* Step 1: Input District */}
      <div className="cm-simulator-grid">
        <div className="cm-simulator-controls-panel">
          <div className="cm-step-indicator">
            <span className="cm-step-number">Step 1</span>
            <h4 className="cm-step-title">Specify Order Delivery District</h4>
          </div>

          <div className="cm-form-group" style={{ marginBottom: '14px' }}>
            <label className="cm-label">Delivery State</label>
            <select value={selectedState} onChange={handleStateChange} className="cm-select">
              {statesList.map((st) => (
                <option key={st.state} value={st.state}>
                  {st.state}
                </option>
              ))}
            </select>
          </div>

          <div className="cm-form-group" style={{ marginBottom: '20px' }}>
            <label className="cm-label">Delivery District</label>
            <select
              value={selectedDistrict}
              onChange={(e) => {
                setSelectedDistrict(e.target.value);
                setHasResolved(false);
                setSelectedBranchForAssignment(null);
                setSnapshotJson(null);
              }}
              className="cm-select"
            >
              {availableDistricts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="cm-btn-primary cm-btn-block"
            onClick={handleRunResolution}
          >
            <Sparkles size={16} /> Resolve Eligible Courier Branches
          </button>

          {/* Architecture note */}
          <div className="cm-arch-note">
            <strong>Operational Resolution Rule:</strong>
            <p>
              District ➔ Active Service Area ➔ Active Branch ➔ Active Courier ➔ Direct Branch Contact
            </p>
            <small>Branch contact number is pulled live, not duplicated into service area.</small>
          </div>
        </div>

        {/* Step 2: Resolution Results */}
        <div className="cm-simulator-results-panel">
          <div className="cm-step-indicator">
            <span className="cm-step-number">Step 2</span>
            <h4 className="cm-step-title">
              Eligible Courier Partners ({eligibleOptions.length})
            </h4>
          </div>

          {!hasResolved ? (
            <div className="cm-simulator-prompt">
              <Search size={36} color="#cbd5e1" />
              <p>Select a state and district, then click "Resolve Eligible Courier Branches".</p>
            </div>
          ) : eligibleOptions.length === 0 ? (
            <div className="cm-simulator-empty">
              <AlertTriangle size={36} color="#f59e0b" />
              <h5>No Active Courier Service for "{selectedDistrict}"</h5>
              <p>
                There are no active courier branches currently assigned to service {selectedDistrict} in{' '}
                {selectedState}.
              </p>
            </div>
          ) : (
            <div className="cm-eligible-list">
              {eligibleOptions.map((opt, idx) => {
                const isSelected =
                  selectedBranchForAssignment?.branchId === opt.branchId &&
                  selectedBranchForAssignment?.courierId === opt.courierId;
                return (
                  <div
                    key={`${opt.courierId}_${opt.branchId}_${idx}`}
                    className={`cm-eligible-card ${isSelected ? 'cm-eligible-card-selected' : ''}`}
                    onClick={() => handleSelectOption(opt)}
                  >
                    <div className="cm-eligible-card-header">
                      <div className="cm-eligible-courier-title">
                        <span className="cm-eligible-rank">#{idx + 1}</span>
                        <div>
                          <strong>{opt.courierName}</strong>
                          <span className="cm-eligible-branch-name">
                            {opt.branchName} ({opt.branchCode})
                          </span>
                        </div>
                      </div>
                      {isSelected ? (
                        <span className="cm-badge cm-badge-success">Selected for Order</span>
                      ) : (
                        <button type="button" className="cm-btn-secondary cm-btn-xs">
                          Select
                        </button>
                      )}
                    </div>

                    <div className="cm-eligible-contact-row">
                      <div className="cm-eligible-contact-item">
                        <Phone size={13} color="#8a6b32" />
                        <span>
                          <strong>Direct Branch Contact:</strong> {opt.contactNumber}
                        </span>
                      </div>
                      {opt.contactPerson && (
                        <div className="cm-eligible-contact-item">
                          <User size={13} color="#64748b" />
                          <span>{opt.contactPerson}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Step 3: Snapshot Display */}
      {snapshotJson && (
        <div className="cm-snapshot-section">
          <div className="cm-snapshot-header">
            <div>
              <h4 className="cm-snapshot-title">
                Order Courier Assignment Snapshot (Section 18)
              </h4>
              <p className="cm-snapshot-subtitle">
                This exact snapshot is permanently stored on the Order document so that contact
                information at the time of assignment is preserved forever even if branch details
                change later.
              </p>
            </div>
            <button
              type="button"
              className="cm-btn-secondary cm-btn-sm"
              onClick={handleCopySnapshot}
            >
              {copied ? <Check size={15} color="#16a34a" /> : <Copy size={15} />}
              {copied ? 'Copied Snapshot' : 'Copy JSON'}
            </button>
          </div>

          <pre className="cm-json-block">
            <code>{JSON.stringify(snapshotJson, null, 2)}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export default OrderAssignmentSimulator;
